import React from "react";
import AccentReadOnlyCheckbox from "../../../../../../../common/FormFields/AccentReadOnlyCheckbox";
import HeadingComponent from "../../HeadingComponent";
import HtmlOrPlainShortDescription from "../../HtmlOrPlainShortDescription";
import { parseAuditedChildOrdinalFromHeading } from "../../../../utils/sectionDisplayOrdinal";

const Attachment = ({
  content,
  globalCount,
  heading,
  treepath,
  showCheckbox,
  isChecked,
  onCheckboxChange,
  disabled = false,
  readOnlyCheckboxTitleMatch = null,
}) => {
  /** App bar "Attachment N" from audit preview (readOnlyCheckboxTitleMatch). */
  const auditBarAttachmentN =
    readOnlyCheckboxTitleMatch?.kind === "attachment" &&
    readOnlyCheckboxTitleMatch.n != null &&
    !Number.isNaN(Number(readOnlyCheckboxTitleMatch.n))
      ? Number(readOnlyCheckboxTitleMatch.n)
      : null;
  /**
   * Selected row in audit preview is the audited node — label must match the bar even when
   * procedure-wide globalIndex is 1 but the audit is "Attachment 2" (per-step / editor numbering).
   */
  const displayHeading = (() => {
    if (auditBarAttachmentN != null && isChecked) {
      return `Attachment ${auditBarAttachmentN}`;
    }
    if (typeof heading === "string" && /^Attachment\s+\d+/i.test(heading.trim())) {
      return heading.trim();
    }
    if (globalCount != null && !Number.isNaN(Number(globalCount))) {
      return `Attachment ${globalCount}`;
    }
    if (typeof heading === "string" && heading.trim()) return heading.trim();
    return "Attachment";
  })();
  const ordinalForHighlight =
    auditBarAttachmentN != null && isChecked
      ? auditBarAttachmentN
      : parseAuditedChildOrdinalFromHeading(heading, "attachment") ??
        (globalCount != null && !Number.isNaN(Number(globalCount)) ? Number(globalCount) : null);
  const emphasizeReadOnlyCheckbox =
    disabled &&
    readOnlyCheckboxTitleMatch?.kind === "attachment" &&
    ordinalForHighlight != null &&
    Number(readOnlyCheckboxTitleMatch.n) === Number(ordinalForHighlight) &&
    (isChecked || false);
  const renderPreview = (attachment) => {
    // API format: attachment is URL string; snapshot format: attachment is object with .attachment (URL)
    const url = typeof attachment === "string" ? attachment : attachment?.attachment;
    if (!url) return null;
    const extension = url?.split(".")?.pop()?.toLowerCase();

    if (["jpg", "jpeg", "png", "gif", "webp"].includes(extension)) {
      return (
        <>
          <img
            src={`${url}`}
            alt={typeof attachment === "object" ? attachment?.short_description : undefined}
            style={{
              maxWidth: "200px",
              marginTop: "20px",
            }}
          />
        </>
      );
    } else if (["mp4", "webm", "ogg"].includes(extension)) {
      return (
        <>
          <video
            controls
            src={`${url}`}
            style={{
              maxWidth: "300px",
              marginTop: "20px",
            }}
          />
        </>
      );
    } else if (["pdf"].includes(extension)) {
      return (
        <>
          <a
            href={`${url}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              marginTop: "20px",
              display: "inline-block",
            }}
          >
            📄 View PDF
          </a>
        </>
      );
    } else {
      return (
        <>
          <a
            href={`${url}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              marginTop: "20px",
              display: "inline-block",
            }}
          >
            📎 View Attachment
          </a>
        </>
      );
    }
  };
  // API format: content.attachments = ["url1", "url2"]; snapshot format: content.attachments = [{ attachment: "url", ... }]
  const rawAttachments = content?.attachments || [];
  const attachmentList = Array.isArray(rawAttachments) ? rawAttachments : [];
  return (
    <div style={{ marginTop: "20px", marginBottom:"20px" }} className="header-spacing">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
        }}
      >
        {showCheckbox &&
          (disabled ? (
            <AccentReadOnlyCheckbox
              readOnlyVisual
              emphasizeChecked={emphasizeReadOnlyCheckbox}
              checked={isChecked || false}
            />
          ) : (
            <input
              type="checkbox"
              checked={isChecked || false}
              onChange={onCheckboxChange}
              style={{
                width: "18px",
                height: "18px",
                cursor: "pointer",
                flexShrink: 0,
              }}
            />
          ))}
        <div style={{ flex: 1, width: "100%", minWidth: 0 }}>
          <HeadingComponent heading={displayHeading} treepath={treepath} />
        </div>
      </div>
      {(content?.short_description != null && content.short_description !== "") && (
        <HtmlOrPlainShortDescription value={content.short_description} />
      )}
      {attachmentList?.map((attachment, i) => (
        <div key={i} style={{ marginLeft: "20px" }}>
          {renderPreview(attachment)}
        </div>
      ))}
    </div>
  );
};

export default Attachment;
